import { Flex, Table, Tag, DatePicker, Button, Space } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import axiosInstance from "../axios";
import { useEffect, useState } from "react";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST = "Asia/Kolkata";

const { RangePicker } = DatePicker;

const PaymentFailed = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [dateRange, setDateRange] = useState([null, null]);

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (page, limit, fromDate, toDate) => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const response = await axiosInstance.get(`/v1/cre/razorpay/payment-failed`, {
        params,
      });

      // API response: { success, message, data: { records: [...], pagination: { total, page, limit, total_pages } } }
      const records = response.data?.data?.records || [];
      const paginationMeta = response.data?.data?.pagination || {};

      setData(
        records.map((item) => ({
          ...item,
          createdAt: item.created_at
            ? dayjs.utc(item.created_at).tz(IST).format("DD-MM-YYYY HH:mm:ss")
            : "-",
          fullName:
            item.user_first_name || item.user_last_name
              ? `${item.user_first_name || ""} ${item.user_last_name || ""}`.trim()
              : null,
        }))
      );
      setPagination((prev) => ({
        ...prev,
        current: paginationMeta.page ?? page,
        pageSize: paginationMeta.limit ?? limit,
        total: paginationMeta.total ?? records.length,
      }));
    } catch (error) {
      console.error("Error fetching payment failed data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pag) => {
    const [from, to] = dateRange;
    fetchData(
      pag.current,
      pag.pageSize,
      from ? from.format("YYYY-MM-DD") : undefined,
      to ? to.format("YYYY-MM-DD") : undefined
    );
  };

  const handleDateSearch = () => {
    const [from, to] = dateRange;
    fetchData(
      1,
      pagination.pageSize,
      from ? from.format("YYYY-MM-DD") : undefined,
      to ? to.format("YYYY-MM-DD") : undefined
    );
  };

  const handleClear = () => {
    setDateRange([null, null]);
    fetchData(1, pagination.pageSize);
  };

  const columns = [
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
    },
    {
      title: "Name",
      key: "fullName",
      width: 160,
      render: (_, record) => (
        <span style={{ textTransform: "capitalize" }}>
          {record.fullName || "-"}
        </span>
      ),
    },
    {
      title: "Phone",
      dataIndex: "user_phone",
      key: "user_phone",
      width: 130,
      render: (val) => val || "-",
    },
    {
      title: "Email",
      dataIndex: "user_email",
      key: "user_email",
      width: 210,
      render: (val) => val || "-",
    },
    {
      title: "Amount (₹)",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (val) =>
        val !== undefined && val !== null
          ? `₹${parseFloat(val).toFixed(2)}`
          : "-",
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      width: 90,
      render: (val) => val || "-",
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      width: 120,
      render: (val) =>
        val ? (
          <Tag color="blue" style={{ textTransform: "capitalize" }}>
            {val}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Razorpay Payment ID",
      dataIndex: "razorpay_payment_id",
      key: "razorpay_payment_id",
      width: 210,
      render: (val) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{val || "-"}</span>
      ),
    },
    {
      title: "Error Code",
      dataIndex: "error_code",
      key: "error_code",
      width: 180,
      render: (val) => (val ? <Tag color="red">{val}</Tag> : "-"),
    },
    {
      title: "Error Description",
      dataIndex: "error_description",
      key: "error_description",
      render: (val) => (
        <span style={{ color: val ? "#cf1322" : "inherit", fontSize: 12 }}>
          {val || "-"}
        </span>
      ),
    },
  ];

  return (
    <>
      <Flex
        style={{
          padding: "12px 16px",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
        align="center"
      >
        <RangePicker
          value={dateRange}
          format="DD-MM-YYYY"
          onChange={(dates) => setDateRange(dates || [null, null])}
          placeholder={["From Date", "To Date"]}
        />
        <Space>
          <Button type="primary" onClick={handleDateSearch}>
            Search
          </Button>
          <Button onClick={handleClear}>Clear</Button>
        </Space>
      </Flex>
      <Flex style={{ overflow: "scroll", marginLeft: "5px" }}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey={(record) =>
            record.id ?? record.razorpay_payment_id ?? Math.random()
          }
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: [10, 15, 20, 25, 50, 100, 150, 200, 250, 500],
          }}
          loading={loading}
          onChange={handleTableChange}
        />
      </Flex>
    </>
  );
};

export default PaymentFailed;
